<!-- DataCollectionCard.vue -->
<template>
    <div
        v-if="hasValidData"
        class="eligibility-tracking-card"
        :class="{ 'is-collapsed': !isFullCard, 'is-initial-render': isInitialRender }"
        @click="toggleCard"
        ref="cardRef"
    >
        <!-- 헤더 -->
        <div class="eligibility-tracking-head">
            <h2 class="eligibility-tracking-title h4">{{ Title }}</h2>
            <div class="eligibility-tracking-remain">
                <span class="remain-days caption" v-if="RemainMeasureSlot !== null"
                    >{{ RemainMeasureSlot }}일 동안</span
                >
                <span class="remain-times h4" v-if="RemainMeasureCount !== null">{{ RemainMeasureCount }}회 남음</span>
            </div>
        </div>

        <!-- 진행바 -->
        <div class="eligibility-tracking-progress">
            <div class="progress-fill" :style="{ width: progressPercent }"></div>
        </div>

        <!-- 설명 -->
        <div class="eligibility-tracking-sub">
            {{ Body }}
            <!-- 정확한 월간리포트 분석을 위해
            <p><strong>7일 당 4회 이상</strong>의 검사가 2주 연속</p>
            필요합니다. -->
        </div>

        <!-- 주차별 표 -->
        <div class="eligibility-tracking-weeks" v-if="MonthTrackingStatus">
            <div
                v-for="(w, idx) in MonthTrackingStatus"
                :key="idx"
                class="eligibility-tracking-week-row"
                :class="{
                    'is-non-current': !isCurrentSection(idx),
                    'is-current': isCurrentSection(idx),
                }"
                v-show="!(isCollapseDone && !isCurrentSection(idx))"
            >
                <div
                    class="eligibility-tracking-week-label caption--regular"
                    :class="{ 'is-range-strong': CurrentSection !== null && idx + 1 === CurrentSection }"
                >
                    {{ w.Label }}
                </div>

                <!-- 4칸 -->
                <div class="eligibility-tracking-dots">
                    <div
                        v-for="(state, i) in w.Checkbox"
                        :key="i"
                        class="eligibility-tracking-dot"
                        :class="computedDotClass(w.StatusCode, state)"
                    >
                        <svg
                            viewBox="0 0 24 24"
                            class="eligibility-tracking-check"
                            v-if="w.StatusCode !== 'EMPTY' && state === 1"
                        >
                            <path
                                d="M20.285 6.709a1.2 1.2 0 0 1 .006 1.697l-9.33 9.33a1.2 1.2 0 0 1-1.697 0l-5.249-5.25a1.2 1.2 0 1 1 1.697-1.697l4.4 4.4 8.48-8.48a1.2 1.2 0 0 1 1.693 0z"
                            />
                        </svg>
                    </div>
                </div>
                <div class="eligibility-tracking-dash" v-if="w.StatusCode !== 'EMPTY'"></div>

                <!-- 상태칩 -->
                <span
                    class="eligibility-tracking-chip"
                    :class="{
                        'chip-green-bg': w.StatusCode === 'VALID',
                        'chip-green': w.StatusCode === 'PRE_VALID',
                        'chip-red': w.StatusCode === 'INVALID',
                        'chip-white': w.StatusCode === 'COLLECTING',
                    }"
                >
                    {{ computedStatusCode(w.StatusCode) }}
                </span>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { ReportConditionTrackingModuleSchema } from '@common/openapi-client';
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useUserInteractionLog } from '../../composables/useUserInteractionLog';
const { saveLog } = useUserInteractionLog(false);

const props = withDefaults(
    defineProps<{
        cardData?: ReportConditionTrackingModuleSchema;
        forceCollapsed?: boolean;
    }>(),
    {
        cardData: undefined,
        forceCollapsed: false,
    },
);

// toRefs 대신 computed를 사용하여 반응성 보장
const Title = computed(() => props.cardData?.Title);
const BarPercentile = computed(() => props.cardData?.BarPercentile);
const Body = computed(() => props.cardData?.Body);
const MonthTrackingStatus = computed(() => props.cardData?.MonthTrackingStatus || []);
const RemainMeasureCount = computed(() => props.cardData?.RemainMeasureCount);
const RemainMeasureSlot = computed(() => props.cardData?.RemainMeasureSlot);
// TODO: 타입 수정 필요
const CurrentSection = computed(() => (props.cardData as any)?.CurrentSection);

// 데이터가 유효한지 확인하는 computed
const hasValidData = computed(() => {
    return (
        props.cardData &&
        (props.cardData.Title ||
            props.cardData.Body ||
            props.cardData.BarPercentile !== undefined ||
            (props.cardData.MonthTrackingStatus && props.cardData.MonthTrackingStatus.length > 0) ||
            props.cardData.RemainMeasureCount !== undefined ||
            props.cardData.RemainMeasureSlot !== undefined)
    );
});

const cardRef = ref<HTMLDivElement | null>(null);
const isFullCard = ref(true);
const isCollapseDone = ref(false);
const isAnimating = ref(false);
const isInitialRender = ref(true);

// forceCollapsed prop이 true면 카드를 접힌 상태로 설정
watch(
    () => props.forceCollapsed,
    newValue => {
        if (newValue) {
            isFullCard.value = false;
            isCollapseDone.value = true;
        }
    },
    { immediate: true },
);

const progressPercent = computed(() => `${BarPercentile.value}%`);

let animId: number | null = null;
let animStart = 0;
let animFrom = 0; // 현재 p
let animTo = 0; // 목표 p (펼침: 0, 축소: 1)

const computedStatusCode = (statusCode: string | null | undefined) => {
    if (!statusCode) return '';

    switch (statusCode) {
        case 'VALID':
            return '충분';
        case 'PRE_VALID':
            return '충분';
        case 'INVALID':
            return '부족';
        case 'COLLECTING':
            return '진행중';
        case 'EMPTY':
            return '';
        default:
            return '';
    }
};

const isCurrentSection = (idx: number) => {
    return CurrentSection.value !== null && idx + 1 === CurrentSection.value;
};

// const isCurrentWeek = (label: string) => {
//     const today = new Date().getDate();
//     const [start, end] = label.split('~').map(s => parseInt(s.replace(/[^0-9]/g, ''), 10));

//     return today >= start && today <= end;
// };

const computedDotClass = (statusCode: string | null | undefined, state: number) => {
    if (!statusCode) return '';

    if (state === 1) {
        switch (statusCode) {
            case 'VALID':
                return 'enough';
            case 'PRE_VALID':
                return 'enough';
            case 'INVALID':
                return 'shortage';
            case 'COLLECTING':
                return 'collecting';
            case 'EMPTY':
                return 'shortage';
            default:
                return '';
        }
    } else if (state === 0) {
        return 'shortage';
    }
};

const setProgress = (p: number) => {
    const el = cardRef.value;
    if (!el) return;
    el.style.setProperty('--p', String(p)); // 0~1
};

const getProgress = (): number => {
    const el = cardRef.value;
    if (!el) return 0;
    const v = parseFloat(getComputedStyle(el).getPropertyValue('--p') || '0');
    return Number.isNaN(v) ? 0 : Math.min(Math.max(v, 0), 1);
};

const animateTo = (target: number, duration = 450, easing: (t: number) => number = easeInOutBack) => {
    if (animId) cancelAnimationFrame(animId);

    isAnimating.value = true;
    animStart = performance.now();
    animFrom = getProgress(); // 시작점은 클램프 기준으로
    animTo = target;

    const frame = (now: number) => {
        const t = Math.min((now - animStart) / duration, 1);
        const eased = easing(t);

        // overshoot 허용값: 펼칠 때(0)는 살짝 음수, 닫힐 때(1)는 살짝 1보다 크게
        const overshoot = target === 0 ? -0.04 : 0.08; // 감도는 취향에 맞게 조절

        // 닫힐 때는 반대 방향 overshoot: 살짝 더 작아졌다가(음수 방향) 최종값으로
        const overshootDirection = target === 1 ? -1 : 1;
        const toRaw = target + overshoot * overshootDirection * (1 - t); // 진행할수록 overshoot 줄어듦

        // 보간 결과(클램프 기준) + overshoot를 합쳐 raw로
        const pLinear = animFrom + (animTo - animFrom) * eased;
        const pRaw = pLinear + (toRaw - target) * eased;

        setProgressRaw(pRaw);

        if (t < 1) {
            animId = requestAnimationFrame(frame);
        } else {
            animId = null;
            setProgressRaw(target); // 종료 시엔 정확히 고정
            isCollapseDone.value = target === 1;
            isAnimating.value = false;
        }
    };
    animId = requestAnimationFrame(frame);
};

const toggleCard = async () => {
    if (isAnimating.value) return;

    const willExpand = !isFullCard.value;

    if (willExpand) {
        isCollapseDone.value = false;
        await nextTick();

        // 프라임: 접힌 상태에서 시작
        setProgressRaw(1);
        cardRef.value && cardRef.value.offsetHeight;

        requestAnimationFrame(() => {
            isFullCard.value = true;
            // 펼치기: out-back (튀며 등장)
            animateTo(0, 520, easeOutBack);
        });
        return;
    }

    // 접기: in-back (들어가며 바운스)
    isFullCard.value = false;
    animateTo(1, 520, easeInOutBack);

    saveLog(162, 'CLICK_TRACKING_CARD', '');
};

// 살짝 튀는 back easing
const easeOutBack = (t: number, s = 1.70158) => {
    return 1 + (s + 1) * Math.pow(t - 1, 3) + s * Math.pow(t - 1, 2);
};
// const easeInBack = (t: number, s = 1.70158) => {
//     return (s + 1) * t * t * t - s * t * t;
// };
const easeInOutBack = (t: number, s = 2.2) => {
    const s2 = s * 1.525;
    return t < 0.5
        ? (Math.pow(2 * t, 2) * ((s2 + 1) * 2 * t - s2)) / 2
        : (Math.pow(2 * t - 2, 2) * ((s2 + 1) * (t * 2 - 2) + s2) + 2) / 2;
};

const setProgressRaw = (pRaw: number) => {
    const el = cardRef.value;
    if (!el) return;
    const p = Math.min(Math.max(pRaw, 0), 1); // 클램프된 표시용
    el.style.setProperty('--p_raw', String(pRaw)); // 튕김 허용
    el.style.setProperty('--p', String(p)); // 표시용 안전 범위
};

onMounted(() => {
    setProgress(0);
    // 초기 렌더링 애니메이션
    setTimeout(() => {
        isInitialRender.value = false;
    }, 50);
});

// 외부에서 toggleCard 함수 호출 가능하도록 expose
defineExpose({
    toggleCard,
});
</script>

<style scoped lang="scss">
.eligibility-tracking-card {
    position: relative;
    width: 100%;
    box-sizing: border-box;
    /* 부모 트랜지션과 충돌하지 않도록 기본 transform 제거 */
    /* transform: translateY(0); */
    background: $grey-9;
    color: $primary-white;
    border-radius: 4.5rem;
    padding: 2.5rem 2rem;
    // margin: 0 0.8rem;

    transform-origin: center bottom;

    --p: 0;

    // border-top-left-radius: calc(4.5rem - 2.2rem * var(--p));
    // border-top-right-radius: calc(4.5rem - 2.2rem * var(--p));
    --pop: calc(-1 * var(--p_raw));
    --bounce: calc(1 - 0.02 * var(--p) + 0.08 * var(--pop));

    /* 기본 축소: 접힐수록 2% 축소 / 팝업: 펼칠 때 더 강한 바운스 효과 */
    // transform: scale(var(--bounce)) translateY(calc(-2px * var(--pop)));

    /* 초기 렌더링 슬라이드 업 애니메이션 */
    transition-delay: 1s;
    transition: transform 0.4s ease-out;

    &.is-initial-render {
        /* 부모 트랜지션과 충돌하지 않도록 주석 처리 */
        /* transform: translateY(100%); */
    }

    /* is-collapsed 상태에서도 너비는 확장된 상태와 동일하게 유지 */
    &.is-collapsed {
        width: 100%;
        /* 부모 트랜지션과 충돌하지 않도록 애니메이션 제거 */
        /* animation: bounce-scale 0.4s ease-in-out; */
        /* 부모 트랜지션과 충돌하지 않도록 transform 제거 */
        /* transform: scale(1) translateY(calc(-2px * var(--pop))); */
        transition: transform 0.2s ease-out;
    }
}

/* 바운스 높이 애니메이션 정의 */
@keyframes bounce-scale {
    0% {
        transform: scaleY(1) translateY(0);
    }
    50% {
        transform: scaleY(0.96) translateY(2px);
    }
    100% {
        transform: scaleY(1) translateY(0);
    }
}

.eligibility-tracking-head {
    display: flex;
    align-items: flex-end;
    gap: 1.2rem;
    padding-bottom: 1.2rem;

    @media (max-width: 320px) {
        display: block;
    }

    .eligibility-tracking-title {
        @media (max-width: 475px) {
            font-size: 1.6rem;
        }

        @media (max-width: 398px) {
            font-size: 1.4rem;
        }
        @media (max-width: 360px) {
            font-size: 1.2rem;
            line-height: 150%;
        }
    }
    .remain-times {
        @media (max-width: 475px) {
            font-size: 1.6rem;
        }
        @media (max-width: 398px) {
            font-size: 1.4rem;
        }
        @media (max-width: 360px) {
            font-size: 1.2rem;
            line-height: 150%;
        }
    }
}

.eligibility-tracking-remain {
    margin-left: auto;
    display: flex;
    align-items: center;
    color: $grey-7;
    font-weight: 600;

    .remain-days {
        margin-right: 0.4rem;
    }
    .remain-times {
        color: $primary-white;
    }

    @media (max-width: 320px) {
        display: flex;
        justify-content: flex-end;
    }
}

.eligibility-tracking-progress {
    position: relative;
    height: 0.2rem;
    border-radius: 1rem;
    background: #000;
    overflow: hidden;
    margin-bottom: 0.7rem;

    /* 축소 */
    opacity: calc(1 - 1 * var(--p));
    height: calc(0.2rem * (1 - var(--p)));
    transform-origin: center;

    .progress-fill {
        height: 100%;
        background: linear-gradient(90deg, #20d373 5.07%, #209dd3 91.83%);
        border-radius: 1rem;
        transition: width 400ms ease;
    }
}

.eligibility-tracking-sub {
    box-sizing: border-box;
    color: $grey-7;
    line-height: 1.5;
    font-size: 1.2rem;
    white-space: pre-line;
    word-break: keep-all;
    /* 시각적 위치 살짝 튕김 */
    transform: translateY(calc(-6px * var(--p_raw)));
    // transition: all 0.4s ease;

    p {
        display: inline;
        color: $grey-6;
    }

    /* 부드럽게 접힘 */
    opacity: calc(1 - 1 * var(--p));
    max-height: calc(70px * (1 - var(--p)));
    overflow: hidden;
    // transform: translateY(calc(-6px * var(--p)));
}

.eligibility-tracking-card.is-collapsed {
    width: 100%;
    .eligibility-tracking-weeks {
        padding-top: 0;
        transition: padding-top 0.4s ease;
    }
}

.eligibility-tracking-weeks {
    display: grid;
    gap: calc(0.8rem * (1 - var(--p)));
    transform: translateY(calc(-0.6rem * var(--p_raw)));
    padding-top: 1.8rem;
}
.eligibility-tracking-week-row {
    display: grid;
    grid-template-columns: 8rem auto 1fr auto;
    align-items: center;
    gap: 0.6rem;

    will-change: transform, opacity, max-height, filter;

    &.is-non-current {
        max-height: calc(64px * (1 - var(--p)));
        opacity: calc(1 - 1 * var(--p));
        overflow: hidden;
        /* 위치/블러 튕김 */
        transform: translateY(calc(-6px * var(--p_raw)));
        filter: blur(calc(0.6px * var(--p_raw)));
        pointer-events: none;
    }

    @media (max-width: 390px) {
        grid-template-columns: 6rem 1fr 2rem 6rem;
        gap: 0.4rem;
    }

    @media (max-width: 375px) {
        gap: 0.4rem;
    }
}

.eligibility-tracking-week-label {
    color: $grey-6;
    text-align: center;

    @media (max-width: 390px) {
        font-size: 1.1rem;
    }
}
.is-range-strong {
    color: #ffffff;
    font-weight: 800;
}

.eligibility-tracking-dots {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 0.3rem;

    @media (min-width: 391px) {
        max-width: 20rem;
    }
}
.eligibility-tracking-dash {
    height: 1px;
    background-image: repeating-linear-gradient(to right, $grey-7 0, $grey-7 2px, transparent 2px, transparent 4px);
    // min-width: 2rem;

    // @media (max-width: 390px) {
    //     max-width: 2rem;
    // }
}

.eligibility-tracking-dot {
    width: 100%;
    max-width: 4rem;
    height: 2rem;
    border-radius: 18px;
    aspect-ratio: 2/1;
    background: var(--dot);
    display: grid;
    place-items: center;
    border: 1px solid transparent;
    box-sizing: border-box;

    @media (max-width: 390px) {
        max-width: none;
        height: auto;
    }

    &.enough {
        background: $primary-white;
        opacity: 1;

        .eligibility-tracking-check {
            fill: $primary-blue;
        }
    }
    &.shortage {
        background: $grey-8;

        .eligibility-tracking-check {
            fill: $grey-7;
        }
    }
    &.collecting {
        background: $grey-8;
        border: 1px solid $primary-white;

        .eligibility-tracking-check {
            fill: $primary-white;
        }
    }
}

.eligibility-tracking-check {
    height: 87.5%;
    width: auto;
}

.eligibility-tracking-chip {
    grid-column: 4;
    justify-self: end;
    font-weight: bold;
    font-size: 1.3rem;
    line-height: 150%;
    box-sizing: border-box;

    @media (max-width: 390px) {
        font-size: 1.1rem;
    }

    &.chip-green-bg {
        background: $primary-white;
        background: $secondary-green;
        padding: 0.2rem 1.25rem;
        border-radius: 1rem;
    }
    &.chip-green {
        color: $secondary-green;
        padding: 0.2rem 1.25rem;
        border-radius: 1rem;
    }
    &.chip-red {
        padding: 0.2rem 1.25rem;
        text-align: center;
        color: #ce7a8c;
    }
    &.chip-white {
        padding: 0.2rem 1.25rem 0.2rem 0;
        color: $primary-white;
    }
}
</style>
